import baseColors from './base-tailwind-colors.json';

export default function buildTailwindTheme() {
  const baseConfig = {
    extend: {
      backgroundImage: {
        'top-gradient':
          'radial-gradient(ellipse 136.96% 69.92% at 50.09% -12.42%, var(--tw-gradient-from), var(--tw-gradient-to))',
      },
      borderRadius: {
        3: '3px',
        4: '4px',
        6: '6px',
        8: '8px',
        12: '12px',
        16: '16px',
        'bts-xs': '2px',
        'bts-sm': '4px',
        'bts-base': '6px',
        'bts-md': '8px',
        'bts-lg': '16px',
        'bts-xl': '24px',
        'bts-xxl': '32px',
      },
      boxShadow: {
        component: '0px 2px 3px rgba(13, 25, 42, 0.05)',
        overlay: '0px 4px 8px rgba(13, 25, 42, 0.2)',
      },
      lineHeight: {
        3: '0.75rem',
        4: '1rem',
        5: '1.25rem',
        6: '1.5rem',
        7: '1.75rem',
        8: '2rem',
        9: '2.25rem',
        10: '2.5rem',
      },
    },
    fontSize: {
      xs: ['10px', '1.5'],
      sm: ['12px', '1.5'],
      base: ['14px', '1.5'],
      m: ['16px', '1.3'],
      lg: ['18px', '1.3'],
      xl: ['24px', '1.3'],
      xxl: ['32px', '1.3'],
      xxxl: ['40px', '1.3'],
      4: '4px',
      8: '8px',
      10: '10px',
      12: '12px',
      14: '14px',
      16: '16px',
      20: '20px',
      24: '24px',
      32: '32px',
      40: '40px',
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 600,
      extrabold: 700,
    },
    spacing: {
      px: '1px',
      0: '0',
      1: '1px',
      2: '2px',
      3: '3px',
      4: '4px',
      5: '5px',
      6: '6px',
      8: '8px',
      9: '9px',
      10: '10px',
      12: '12px',
      14: '14px',
      16: '16px',
      18: '18px',
      20: '20px',
      24: '24px',
      25: '25px',
      28: '28px',
      30: '30px',
      32: '32px',
      36: '36px',
      40: '40px',
      44: '44px',
      48: '48px',
      52: '52px',
      56: '56px',
      60: '60px',
      64: '64px',
      68: '68px',
      72: '72px',
      80: '80px',
      95: '95px',
      96: '96px',
      100: '100px',
      120: '120px',
      160: '160px',
      180: '180px',
      240: '240px',
      260: '260px',
      300: '300px',
      350: '350px',
    },
  };

  const {
    blue,
    denim,
    green,
    red,
    neutral,
    orange,
    pink,
    teal,
    violet,
    yellow,
  } = baseColors;

  const ACCENT = 'accent';
  const BACKGROUND = 'background';
  const BACKGROUND_WEAK = 'background-weak';
  const BACKGROUND_MEDIUM = 'background-medium';
  const BACKGROUND_STRONG = 'background-strong';
  const BACKGROUND_DISABLED = 'background-disabled';
  const BACKGROUND_TOOLTIP = 'background-tooltip';

  const BORDER = 'border';
  const BORDER_WEAK = 'border-weak';
  const BORDER_FOCUS = 'border-focus';
  const BORDER_PRESSED = 'border-pressed';

  const TEXT = 'text';
  const TEXT_WEAKEST = 'text-weakest';
  const TEXT_WEAK = 'text-weak';
  const TEXT_STRONG = 'text-strong';
  const TEXT_BUTTON = 'text-button';
  const TEXT_LINK = 'text-link';
  const TEXT_LINK_HOVER = 'text-link-hover';
  const TEXT_PLACEHOLDER = 'text-placeholder';

  const tokenObjects = {
    neutral: {
      ...baseColors.neutral,
      [ACCENT]: neutral[400],
      [BACKGROUND]: neutral[100],
      [BACKGROUND_DISABLED]: neutral[200],
      [BACKGROUND_MEDIUM]: neutral[400],
      [BACKGROUND_WEAK]: neutral[50],
      [BACKGROUND_STRONG]: neutral[900],
      [BACKGROUND_TOOLTIP]: neutral[700],
      [BORDER]: neutral[300],
      [BORDER_WEAK]: neutral[50],
      [BORDER_FOCUS]: blue[300],
      [TEXT]: neutral[600],
      [TEXT_BUTTON]: neutral[600],
      [TEXT_PLACEHOLDER]: neutral[400],
      [TEXT_STRONG]: neutral[900],
      [TEXT_WEAK]: neutral[500],
      [TEXT_WEAKEST]: '#686E78',
    },
    accent: {
      [BACKGROUND_WEAK]: orange[50],
      [BORDER]: orange[300],
      [ACCENT]: orange[400],
      [TEXT]: orange[600],
      [TEXT_STRONG]: orange[900],
    },
    beta: {
      [BACKGROUND_WEAK]: pink[50],
      [BORDER]: pink[300],
      [ACCENT]: pink[400],
      [BACKGROUND_MEDIUM]: pink[400],
      [TEXT]: pink[600],
      [TEXT_STRONG]: pink[900],
    },
    danger: {
      [BACKGROUND_WEAK]: red[50],
      [BORDER_FOCUS]: red[300],
      [BORDER_PRESSED]: red[300], // to be deprecated
      [ACCENT]: red[400],
      [TEXT_BUTTON]: red[600],
      [BACKGROUND_MEDIUM]: red[400],
      [TEXT]: red[600],
      [TEXT_STRONG]: red[900],
    },
    info: {
      [BACKGROUND_WEAK]: teal[50],
      [BACKGROUND]: teal[100],
      [BORDER]: teal[300],
      [ACCENT]: teal[400],
      [TEXT]: teal[600],
      [TEXT_STRONG]: teal[900],
    },
    event: {
      [BACKGROUND_WEAK]: teal[50],
      [BACKGROUND]: teal[100],
      [BORDER]: teal[300],
      [ACCENT]: teal[400],
      [TEXT]: teal[600],
      [TEXT_STRONG]: teal[900],
    },
    primary: {
      [BACKGROUND_WEAK]: blue[50],
      [BORDER]: blue[300],
      [BORDER_PRESSED]: neutral[400], // to be deprecated
      [ACCENT]: blue[400],
      [BACKGROUND_MEDIUM]: blue[400],
      [TEXT_BUTTON]: blue[800],
      [TEXT]: blue[600],
      [TEXT_STRONG]: blue[900],
      [TEXT_LINK_HOVER]: denim[300],
    },
    secondary: {
      [BACKGROUND_WEAK]: denim[50],
      [BACKGROUND]: denim[100],
      [BACKGROUND_MEDIUM]: denim[300],
      [BORDER]: denim[300],
      [ACCENT]: denim[400],
      [TEXT]: denim[600],
      [TEXT_LINK]: denim[600],
      [TEXT_LINK_HOVER]: denim[300],
      [TEXT_STRONG]: denim[900],
    },
    success: {
      [BACKGROUND_WEAK]: green[50],
      [BACKGROUND]: green[100],
      [BORDER]: green[300],
      [BORDER_FOCUS]: green[300],
      [BACKGROUND_MEDIUM]: green[400],
      [ACCENT]: green[400],
      [TEXT]: green[600],
      [TEXT_STRONG]: green[900],
    },
    upsell: {
      [BACKGROUND_WEAK]: violet[50],
      [BACKGROUND]: violet[200],
      [BORDER]: violet[300],
      [BACKGROUND_MEDIUM]: violet[400],
      [ACCENT]: violet[400],
      [TEXT]: violet[600],
      [TEXT_STRONG]: violet[900],
    },
    warning: {
      [BACKGROUND_WEAK]: yellow[50],
      [BACKGROUND]: yellow[100],
      [BORDER]: yellow[300],
      [BORDER_FOCUS]: yellow[300],
      [ACCENT]: yellow[500],
      [TEXT]: yellow[700],
      [TEXT_STRONG]: yellow[900],
    },
  };

  // Flatten the color structure for Tailwind CSS v4
  const flattenedColors: Record<string, string> = {};

  // Add base colors
  Object.entries(baseColors).forEach(([colorName, colorValues]) => {
    if (typeof colorValues === 'object' && colorValues !== null) {
      Object.entries(colorValues).forEach(([shade, value]) => {
        flattenedColors[`${colorName}-${shade}`] = value;
      });
    } else {
      flattenedColors[colorName] = colorValues;
    }
  });

  // Add token objects (primary, neutral, etc.)
  Object.entries(tokenObjects).forEach(([tokenName, tokenValues]) => {
    Object.entries(tokenValues).forEach(([shade, value]) => {
      flattenedColors[`${tokenName}-${shade}`] = value;
    });
  });

  // Add special colors
  flattenedColors['brand-blue'] = denim[400];

  const theme = {
    ...baseConfig,
    colors: flattenedColors,
  } as const;
  type TailwindTheme = typeof theme;
  return theme as TailwindTheme;
}
